/**
 * RecordLinks (CC-LINKS stage 1) — the inline, per-record linkage affordance.
 *
 * Stage 2 (the cross-workshop `TraceabilityModal`) already creates/browses
 * links from one place; stage 1 is the *within-record* convenience: on a
 * record's read-through row, show the links touching it as click-to-navigate
 * chips and let the facilitator add one without leaving the row. There is no
 * per-record `links` array — every link lives in the single global `useLinks`
 * store, and reverse edges are computed on read (`linksTouching`), so this is
 * a thin view over the same source the modal uses, never a second copy.
 *
 * The picker is scoped to the *current* workshop (stage 1 = within-workshop)
 * via `listLinkableEntities(workshopId)`, excluding the record itself.
 */

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { LINK_RELATIONS, LINK_RELATION_DEFAULT, type LinkRelation } from "@/lib/taxonomy";
import {
  LINK_ENDPOINT_LABEL,
  LINK_RELATION_LABEL,
  linksTouching,
  listLinkableEntities,
  resolveEndpoint,
  sameEndpoint,
} from "@/lib/links";
import type { SearchCategory } from "@/lib/search";
import { useLinks, type LinkEndpoint } from "@/stores/useLinks";
import { useJumpTarget } from "@/features/search/useJumpTarget";
import { useWorkshopModals, type OpenModal } from "@/features/workshop/modals/useWorkshopModals";

/** Route a hit's category to the modal that shows it. Mirrors the same map
 *  in `TraceabilityModal` (one entry point, kept identical). */
function modalForCategory(category: SearchCategory): Exclude<OpenModal, null> | null {
  if (category === "capture") return "captures";
  if (category === "transcript") return "transcript";
  if (category === "upload") return null;
  return "decision-log";
}

const encode = (e: LinkEndpoint): string => `${e.kind}::${e.workshopId}::${e.id}`;

const CHIP_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  maxWidth: 220,
  padding: "1px 7px",
  borderRadius: 6,
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.22)",
  background: "rgba(var(--accent-true-blue-rgb), 0.06)",
  fontSize: "var(--fs-micro)",
} as const;

export function RecordLinks({ endpoint }: { endpoint: LinkEndpoint }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const openModal = useWorkshopModals((s) => s.open);
  const auth = useAuth();
  const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";

  const links = useLinks((s) => s.links);
  const addLink = useLinks((s) => s.add);
  const removeLink = useLinks((s) => s.remove);

  const [adding, setAdding] = useState(false);
  const [toKey, setToKey] = useState("");
  const [relation, setRelation] = useState<LinkRelation>(LINK_RELATION_DEFAULT);

  const { kind, workshopId, id } = endpoint;
  const touching = useMemo(
    () => linksTouching(links, { kind, workshopId, id }),
    [links, kind, workshopId, id],
  );

  // Within-workshop targets (stage 1), excluding the record itself. Computed
  // only while the picker is open; reads live store snapshots.
  const targets = useMemo(
    () =>
      adding
        ? listLinkableEntities(workshopId).filter(
            (e) => !sameEndpoint(e.endpoint, { kind, workshopId, id }),
          )
        : [],
    [adding, kind, workshopId, id],
  );
  const byKey = useMemo(() => {
    const m = new Map<string, (typeof targets)[number]>();
    for (const t of targets) m.set(encode(t.endpoint), t);
    return m;
  }, [targets]);

  function goTo(e: LinkEndpoint): void {
    const hit = resolveEndpoint(e);
    if (!hit) return;
    useJumpTarget.getState().set({
      workshopId: e.workshopId,
      category: hit.category,
      id: hit.id,
    });
    if (e.workshopId !== workshopId) {
      navigate(`/workshop/${e.workshopId}`, { state: { from: pathname + search } });
    }
    const target = modalForCategory(hit.category);
    if (target) {
      requestAnimationFrame(() => openModal(target));
    }
  }

  function submit(): void {
    const to = byKey.get(toKey)?.endpoint;
    if (!to) return;
    addLink({
      id: crypto.randomUUID(),
      from: { kind, workshopId, id },
      to,
      relation,
      createdAt: Date.now(),
      addedBy,
    });
    setToKey("");
    setRelation(LINK_RELATION_DEFAULT);
    setAdding(false);
  }

  return (
    <div
      className="ig-record-links"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5, marginTop: 4 }}
    >
      {touching.map(({ link, other, direction }) => {
        const hit = resolveEndpoint(other);
        const dangling = hit == null;
        return (
          <span key={link.id} style={CHIP_STYLE}>
            <button
              type="button"
              onClick={() => goTo(other)}
              disabled={dangling}
              title={`${direction === "in" ? "Linked from" : "Links to"} ${LINK_ENDPOINT_LABEL[other.kind]} · ${other.workshopId}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                border: 0,
                background: "transparent",
                padding: 0,
                maxWidth: 180,
                color: dangling ? "var(--text-muted)" : "var(--text-primary)",
                cursor: dangling ? "default" : "pointer",
                fontSize: "var(--fs-micro)",
              }}
            >
              <ArrowRight
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  color: "var(--accent-sky)",
                  transform: direction === "in" ? "rotate(180deg)" : undefined,
                }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {hit?.title ?? "(removed)"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => removeLink(link.id)}
              aria-label="Remove link"
              style={{ display: "inline-flex", border: 0, background: "transparent", padding: 0, cursor: "pointer", color: "var(--text-muted)" }}
            >
              <X aria-hidden style={{ width: 10, height: 10 }} />
            </button>
          </span>
        );
      })}

      {adding ? (
        <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}>
          <select
            aria-label="Link relation"
            value={relation}
            onChange={(e) => setRelation(e.target.value as LinkRelation)}
            className={cn(FIELD_CLASS, "h-7 py-0 text-xs")}
            style={{ width: 130, flex: "none" }}
          >
            {LINK_RELATIONS.map((r) => (
              <option key={r} value={r}>
                {LINK_RELATION_LABEL[r]}
              </option>
            ))}
          </select>
          <select
            aria-label="Link target record"
            value={toKey}
            onChange={(e) => setToKey(e.target.value)}
            className={cn(FIELD_CLASS, "h-7 py-0 text-xs")}
            style={{ minWidth: 170 }}
          >
            <option value="">Link to…</option>
            {targets.map(({ endpoint: ep, hit }) => (
              <option key={encode(ep)} value={encode(ep)}>
                {LINK_ENDPOINT_LABEL[ep.kind]} · {hit.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={submit}
            disabled={!toKey}
            className="modal-close-btn inline-flex items-center gap-1"
            style={{ flex: "none", opacity: toKey ? 1 : 0.5, cursor: toKey ? "pointer" : "default" }}
          >
            <Plus aria-hidden style={{ width: 11, height: 11 }} strokeWidth={2.5} /> LINK
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setToKey("");
            }}
            aria-label="Cancel link"
            style={{ display: "inline-flex", border: 0, background: "transparent", padding: 0, cursor: "pointer", color: "var(--text-muted)" }}
          >
            <X aria-hidden style={{ width: 12, height: 12 }} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1"
          style={{
            border: "1px dashed rgba(var(--accent-true-blue-rgb), 0.3)",
            background: "transparent",
            borderRadius: 6,
            padding: "1px 7px",
            cursor: "pointer",
            color: "var(--text-muted)",
            fontSize: "var(--fs-micro)",
          }}
        >
          <Plus aria-hidden style={{ width: 10, height: 10 }} /> link
        </button>
      )}
    </div>
  );
}
